import apiClient from './api';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787/api';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  type?: 'compact' | 'mode_switch';
  content: string;
  tool_calls?: Array<{ tool: string; params: any; result: any }>;
  activeTools?: string[];
  isStreaming?: boolean;
  usage?: { input_tokens: number; output_tokens: number; cost_usd?: number; mode?: string; model?: string };
  compacted_count?: number;
  timestamp: Date;
}

export interface ConversationSummary {
  id: string;
  title: string | null;
  user_id: string;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  message_count: number;
}

export interface StreamCallbacks {
  onTextDelta: (text: string) => void;
  onToolStart: (tool: string) => void;
  onToolResult: (data: { tool: string; success: boolean }) => void;
  onModeSwitch: (data: { mode: string; auto: boolean }) => void;
  onMode: (data: { mode: string; model: string }) => void;
  onDone: (data: { tool_calls: any[]; usage: any; mode: string }) => void;
  onError: (error: string) => void;
}

class CoworkService {
  // ── Legacy single-session endpoints (kept for backward compat) ──

  async sendMessage(message: string, context?: { page?: string; agent_id?: string }) {
    const response = await apiClient.post('/assistant/chat', { message, context });
    return response.data.data;
  }

  async sendMessageStream(
    message: string,
    context: { page?: string; agent_id?: string } | undefined,
    callbacks: StreamCallbacks
  ): Promise<void> {
    const token = localStorage.getItem('access_token');

    const response = await fetch(`${API_BASE}/assistant/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ message, context }),
    });

    if (!response.ok) {
      callbacks.onError(`Request failed: ${response.status}`);
      return;
    }

    await this._readSSEStream(response, callbacks);
  }

  async getHistory() {
    const response = await apiClient.get('/assistant/history');
    return response.data.data?.messages || [];
  }

  async clearHistory() {
    await apiClient.delete('/assistant/history');
  }

  // ── Multi-conversation endpoints ──

  async listConversations(limit = 50, offset = 0): Promise<{ conversations: ConversationSummary[]; total: number }> {
    const response = await apiClient.get('/assistant/conversations', { params: { limit, offset } });
    return response.data.data || response.data;
  }

  async createConversation(title?: string): Promise<ConversationSummary> {
    const response = await apiClient.post('/assistant/conversations', { title });
    return response.data.data || response.data;
  }

  async getConversationMessages(conversationId: string): Promise<any[]> {
    const response = await apiClient.get(`/assistant/conversations/${conversationId}/messages`);
    return response.data.data?.messages || response.data.messages || [];
  }

  async sendMessageStreamInConversation(
    conversationId: string,
    message: string,
    context: any,
    callbacks: StreamCallbacks,
    mode?: string,
  ): Promise<void> {
    const token = localStorage.getItem('access_token');

    const response = await fetch(`${API_BASE}/assistant/conversations/${conversationId}/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ message, context, mode }),
    });

    if (!response.ok) {
      callbacks.onError(`Request failed: ${response.status}`);
      return;
    }

    await this._readSSEStream(response, callbacks);
  }

  async updateTitle(conversationId: string, title: string): Promise<void> {
    await apiClient.put(`/assistant/conversations/${conversationId}/title`, { title });
  }

  async deleteConversation(conversationId: string): Promise<void> {
    await apiClient.delete(`/assistant/conversations/${conversationId}`);
  }

  // ── Shared SSE reader ──

  private async _readSSEStream(response: Response, callbacks: StreamCallbacks): Promise<void> {
    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      let currentEvent = '';
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7);
        } else if (line.startsWith('data: ')) {
          const jsonStr = line.slice(6);
          try {
            const data = JSON.parse(jsonStr);
            switch (currentEvent) {
              case 'text_delta':
                callbacks.onTextDelta(data.text);
                break;
              case 'tool_start':
                callbacks.onToolStart(data.tool);
                break;
              case 'tool_result':
                callbacks.onToolResult(data);
                break;
              case 'mode_switch':
                callbacks.onModeSwitch(data);
                break;
              case 'mode':
                callbacks.onMode(data);
                break;
              case 'done':
                callbacks.onDone(data);
                break;
              case 'error':
                callbacks.onError(data.error);
                break;
            }
          } catch {
            // skip unparseable
          }
          currentEvent = '';
        }
      }
    }
  }
}

export const coworkService = new CoworkService();
export default coworkService;
